import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Collapse,
  Form,
  Input,
  InputNumber,
  Modal,
  Segmented,
  Space,
  Spin,
  Switch,
  Table,
  Tooltip,
  message as toast,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { LockOutlined } from '@ant-design/icons';
import FileUpload from 'components/file-upload';
import { UPLOAD_TYPE } from 'constants/common';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import { colors } from 'theme/tokens';
import { useFeed } from './shared/context';
import { PersonAvatar, Pill } from './shared/components';
import {
  AI_TERMS_CLAUSE,
  CLASS_HINT,
  CLASS_LABEL,
  EXEMPT_OPTIONS,
  MAINTENANCE_TASKS,
} from './shared/labels';
import { EditableFeedClass, FeedClass, FeedSettingsView, FeedTermsVersionRow } from './shared/types';
import {
  errorCode,
  feedError,
  fmtBytes,
  fmtDateTime,
  personText,
  setFeedSettingsCache,
  useFeedList,
  useUrlFilters,
} from './shared/utils';
import { AiSection, ConfirmSwitch, thresholdsForSave } from './settings-ai';
import WordsCard from './words-card';
import styles from './styles.module.scss';

type SectionKey = 'permissions' | 'media' | 'limits' | 'ai' | 'words' | 'terms' | 'server';

const SECTIONS: { key: SectionKey; label: string; adminOnly?: boolean }[] = [
  { key: 'permissions', label: 'Quyền' },
  { key: 'media', label: 'Ảnh & video' },
  { key: 'limits', label: 'Giới hạn & báo cáo' },
  { key: 'ai', label: 'Kiểm duyệt AI' },
  { key: 'words', label: 'Từ cấm' },
  { key: 'terms', label: 'Điều khoản & trang HP Media' },
  { key: 'server', label: 'Máy chủ', adminOnly: true },
];

/** Trường → phần chứa nó (lỗi FEED_SETTINGS_INVALID có `field` thì nhảy tới đúng phần). */
const FIELD_SECTION: Record<string, SectionKey> = {
  permissions: 'permissions',
  holdOnReview: 'permissions',
  reviewExempt: 'permissions',
  videoEnabled: 'media',
  videoMaxSeconds: 'media',
  videoMaxMb: 'media',
  imageMaxCount: 'media',
  imageMaxMb: 'media',
  textMaxLen: 'limits',
  commentMaxLen: 'limits',
  postsPerHour: 'limits',
  commentsPerHour: 'limits',
  reportAutoHideThreshold: 'limits',
  importantDefaultDays: 'limits',
  importantPushPerDay: 'limits',
  aiImagesEnabled: 'ai',
  aiTextEnabled: 'ai',
  aiCommentsEnabled: 'ai',
  aiFailMode: 'ai',
  aiVideoFrames: 'ai',
  aiThresholds: 'ai',
  aiImageProviders: 'ai',
  aiTextProviders: 'ai',
  termsText: 'terms',
  termsHasAiClause: 'terms',
  bumpTermsVersion: 'terms',
  pageName: 'terms',
  pageAvatarUrl: 'terms',
};

const EDITABLE_KEYS = Object.keys(FIELD_SECTION).filter((k) => k !== 'bumpTermsVersion');
const EDITABLE_CLASSES: EditableFeedClass[] = ['AGENCY', 'CREATOR', 'STAFF'];
const PERM_KEYS = ['view', 'post', 'comment', 'react'] as const;

const toForm = (s: FeedSettingsView): Record<string, any> => {
  const values: Record<string, any> = {};
  EDITABLE_KEYS.forEach((k) => {
    values[k] = (s as any)[k];
  });
  values.termsText = s.termsText ?? null;
  values.aiImageProviders = s.aiImageProviders ?? null;
  values.aiTextProviders = s.aiTextProviders ?? null;
  values.pageAvatarUrl = s.pageAvatarUrl ?? null;
  values.reviewExempt = s.reviewExempt || [];
  const perms: Record<string, any> = {};
  EDITABLE_CLASSES.forEach((c) => {
    const p = s.permissions?.[c] || s.effectivePermissions?.[c] || { view: true, post: true, comment: true, react: true };
    perms[c] = { view: !!p.view, post: !!p.post, comment: !!p.comment, react: !!p.react };
  });
  values.permissions = perms;
  values.bumpTermsVersion = false;
  return values;
};

const same = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/** Chỉ các trường đã đổi so với lần tải (03 §11.1) — hai Admin sửa hai phần khác nhau không đè nhau. */
const diffValues = (initial: Record<string, any>, current: Record<string, any>) => {
  const body: Record<string, any> = {};
  EDITABLE_KEYS.forEach((k) => {
    if (!(k in current)) return;
    if (!same(initial[k], current[k])) body[k] = current[k];
  });
  if (current.bumpTermsVersion) body.bumpTermsVersion = true;
  if (typeof body.termsText === 'string') body.termsText = body.termsText.replace(/\r\n/g, '\n');
  if (typeof body.pageName === 'string') body.pageName = body.pageName.trim();
  if ('aiThresholds' in body) body.aiThresholds = thresholdsForSave(body.aiThresholds);
  return body;
};

// ------------------------------------------------------------------ ô tuỳ biến

/** Ảnh đại diện trang: lưu URL đầy đủ (FileUpload trả khoá + URL). */
const AvatarUrlInput = ({
  value,
  onChange,
  disabled,
  name,
}: {
  value?: string | null;
  onChange?: (v: string | null) => void;
  disabled?: boolean;
  name?: string;
}) => (
  <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
    <PersonAvatar name={name || 'HP Media'} avatar={value || null} size={56} />
    {!disabled && (
      <FileUpload
        type={UPLOAD_TYPE.IMAGE}
        maxFileSize={5}
        value={value ? ({ fileKey: value, fileUrl: value } as any) : undefined}
        onChange={(v: any) => {
          if (v?.error) toast.error(v.error);
          onChange?.(v?.fileUrl ? v.fileUrl : null);
        }}
      />
    )}
    {!disabled && value && (
      <Button size="small" onClick={() => onChange?.(null)}>
        Bỏ ảnh
      </Button>
    )}
  </div>
);

/** Văn bản Quy tắc: null = dùng mặc định của máy chủ. */
const TermsTextInput = ({
  value,
  onChange,
  disabled,
  defaultTerms,
}: {
  value?: string | null;
  onChange?: (v: string | null) => void;
  disabled?: boolean;
  defaultTerms?: string;
}) => {
  const form = Form.useFormInstance();
  if (value === null || value === undefined) {
    return (
      <div>
        <Space style={{ marginBottom: 8 }} wrap>
          <Pill color={colors.infoInk} bg={colors.infoSoft}>
            Đang dùng mặc định
          </Pill>
          {!disabled && (
            <Button size="small" onClick={() => onChange?.(defaultTerms || '')}>
              Tự viết điều khoản (bắt đầu từ bản mặc định)
            </Button>
          )}
        </Space>
        <div className={styles.termsText}>{defaultTerms || '(máy chủ chưa trả văn bản mặc định)'}</div>
      </div>
    );
  }
  return (
    <div>
      <Input.TextArea
        value={value}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.value)}
        autoSize={{ minRows: 8, maxRows: 20 }}
        maxLength={20000}
        showCount
      />
      {!disabled && (
        <Space style={{ marginTop: 8 }} wrap>
          <Button
            size="small"
            disabled={value.includes(AI_TERMS_CLAUSE)}
            onClick={() => {
              onChange?.(`${value.replace(/\s+$/, '')}${value.trim() ? '\n\n' : ''}${AI_TERMS_CLAUSE}`);
              // Vừa chèn đúng câu mẫu → tick sẵn "có câu về AI" (Admin vẫn bỏ tick được).
              form.setFieldValue('termsHasAiClause', true);
            }}
          >
            Chèn câu mẫu về AI
          </Button>
          <Button size="small" onClick={() => onChange?.(null)}>
            Dùng điều khoản mặc định
          </Button>
        </Space>
      )}
    </div>
  );
};

// ------------------------------------------------------------------ phần Quyền

interface PermRow {
  cls: FeedClass;
  locked: boolean;
}

const PERM_ROWS: PermRow[] = [
  { cls: 'MANAGEMENT', locked: true },
  { cls: 'AGENCY', locked: false },
  { cls: 'CREATOR', locked: false },
  { cls: 'STAFF', locked: false },
  { cls: 'PUBLIC', locked: true },
];

const PERM_ROW_TITLE: Record<FeedClass, string> = {
  MANAGEMENT: 'Quản lý (ADMIN, MOD)',
  AGENCY: 'Agency',
  CREATOR: 'Creator (gồm Studio Creator)',
  STAFF: 'Nhân viên (đã xác nhận)',
  PUBLIC: 'User (fan, khách)',
};

const LOCK_TIP: Partial<Record<FeedClass, string>> = {
  MANAGEMENT: 'Quản lý luôn đủ quyền.',
  PUBLIC:
    'Khoá cứng theo quyết định 28/09: fan/khách không xem được Bảng tin (App hiện màn Không có quyền truy cập).',
};

const PermissionsTable = ({ readOnly }: { readOnly: boolean }) => {
  const permCell = (key: typeof PERM_KEYS[number]) => (_: unknown, row: PermRow) => {
    if (row.locked) {
      return (
        <Tooltip title={LOCK_TIP[row.cls]}>
          <span className={styles.checkCell}>
            <Checkbox checked={row.cls === 'MANAGEMENT'} disabled />
          </span>
        </Tooltip>
      );
    }
    return (
      <div className={styles.checkCell}>
        <Form.Item noStyle shouldUpdate>
          {({ getFieldValue }) => {
            const viewOn = getFieldValue(['permissions', row.cls, 'view']);
            return (
              <Form.Item noStyle name={['permissions', row.cls, key]} valuePropName="checked">
                <Checkbox disabled={readOnly || (key !== 'view' && !viewOn)} />
              </Form.Item>
            );
          }}
        </Form.Item>
      </div>
    );
  };
  const columns: ColumnsType<PermRow> = [
    {
      title: 'Lớp',
      key: 'cls',
      render: (_, row) => (
        <div style={{ minWidth: 280 }}>
          <div style={{ fontWeight: 600 }}>
            {row.locked && <LockOutlined style={{ marginRight: 6, color: colors.textMuted }} />}
            {PERM_ROW_TITLE[row.cls] || CLASS_LABEL[row.cls]}
          </div>
          <div className={styles.muted}>{CLASS_HINT[row.cls]}</div>
        </div>
      ),
    },
    { title: 'Xem', key: 'view', width: 88, align: 'center', render: permCell('view') },
    { title: 'Đăng bài', key: 'post', width: 96, align: 'center', render: permCell('post') },
    { title: 'Bình luận', key: 'comment', width: 112, align: 'center', render: permCell('comment') },
    { title: 'Thả cảm xúc', key: 'react', width: 112, align: 'center', render: permCell('react') },
  ];
  return (
    <div className="custom-table">
      <Table<PermRow>
        rowKey="cls"
        size="middle"
        pagination={false}
        dataSource={PERM_ROWS}
        columns={columns}
        scroll={{ x: 700 }}
      />
    </div>
  );
};

// ------------------------------------------------------------------ máy chủ (D30)

const MaintenanceSection = () => {
  const [running, setRunning] = useState<string | null>(null);
  const [recountAll, setRecountAll] = useState(false);
  const [output, setOutput] = useState<string | null>(null);
  const runTask = async (task: string, label: string) => {
    setRunning(task);
    try {
      const body = task === 'recount' && recountAll ? { task, all: true } : { task };
      const res: any = await AxiosUtils.post(API.feedAdminMaintenance, body);
      setOutput(JSON.stringify(res?.data ?? null, null, 2));
      toast.success(`Đã chạy: ${label}.`);
    } catch (err) {
      toast.error(feedError(err));
    } finally {
      setRunning(null);
    }
  };
  return (
    <div>
      <div className={styles.hint} style={{ marginBottom: 12 }}>
        Máy chủ thật tự chạy các việc này theo lịch. Nút ở đây dùng khi máy dev tắt lịch (CRON_ENABLED=false) hoặc
        cần chạy ngay.
      </div>
      <div className="custom-table">
        <Table
          rowKey="task"
          size="middle"
          pagination={false}
          dataSource={MAINTENANCE_TASKS}
          scroll={{ x: 640 }}
          columns={[
            {
              title: 'Việc',
              key: 'label',
              render: (_, t) => (
                <div>
                  <div style={{ fontWeight: 600 }}>{t.label}</div>
                  {t.hint && <div className={styles.muted}>{t.hint}</div>}
                  {t.task === 'recount' && (
                    <Checkbox checked={recountAll} onChange={(e) => setRecountAll(e.target.checked)}>
                      Toàn bộ (không chỉ 30 ngày)
                    </Checkbox>
                  )}
                </div>
              ),
            },
            {
              title: '',
              key: 'run',
              width: 96,
              render: (_, t) => (
                <Button size="small" loading={running === t.task} onClick={() => runTask(t.task, t.label)}>
                  Chạy
                </Button>
              ),
            },
          ]}
        />
      </div>
      {output && <pre className={styles.pre} style={{ marginTop: 12 }}>{output}</pre>}
    </div>
  );
};

// ------------------------------------------------------------------ lịch sử phiên bản Quy tắc (D38)

const TermsVersions = () => {
  const { refreshKey } = useFeed();
  const { items, loading } = useFeedList<FeedTermsVersionRow>(API.feedAdminTermsVersions, {}, { refreshKey });
  const [viewing, setViewing] = useState<FeedTermsVersionRow | null>(null);
  return (
    <>
      <div className="custom-table">
        <Table<FeedTermsVersionRow>
          rowKey="version"
          size="small"
          loading={loading}
          pagination={false}
          dataSource={[...items].sort((a, b) => b.version - a.version)}
          locale={{ emptyText: 'Chưa có phiên bản nào.' }}
          scroll={{ x: 640 }}
          columns={[
            { title: 'Phiên bản', key: 'v', width: 96, render: (_, r) => `v${r.version}` },
            { title: 'Có câu AI', key: 'ai', width: 96, render: (_, r) => (r.hasAiClause ? 'Có' : 'Không') },
            { title: 'Tạo bởi', key: 'by', render: (_, r) => (r.createdBy ? personText(r.createdBy) : 'Hệ thống') },
            { title: 'Lúc', key: 'at', width: 144, render: (_, r) => fmtDateTime(r.createdAt) },
            { title: 'Đã đồng ý', key: 'n', width: 104, render: (_, r) => `${r.acceptedCount ?? 0} người` },
            {
              title: '',
              key: 'view',
              width: 120,
              render: (_, r) =>
                r.text ? (
                  <Button size="small" onClick={() => setViewing(r)}>
                    Xem văn bản
                  </Button>
                ) : null,
            },
          ]}
        />
      </div>
      <Modal
        open={!!viewing}
        title={viewing ? `Quy tắc đăng bài v${viewing.version}` : ''}
        footer={null}
        onCancel={() => setViewing(null)}
        width={640}
        destroyOnHidden
      >
        <div className={styles.termsText}>{viewing?.text}</div>
      </Modal>
    </>
  );
};

// ------------------------------------------------------------------ tab

/** Tab "Cài đặt" (03 §11): 1 form, 7 phần, thanh Lưu dính đáy. MOD chỉ xem. */
const SettingsTab = () => {
  const { isAdmin, summary, afterAction, reloadAccess } = useFeed();
  const { get, set } = useUrlFilters();
  const [form] = Form.useForm();
  const [settings, setSettings] = useState<FeedSettingsView | null>(null);
  const [initial, setInitial] = useState<Record<string, any>>({});
  const [dirtyCount, setDirtyCount] = useState(0);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [tick, setTick] = useState(0);

  const requested = get('section') as SectionKey | undefined;
  const visibleSections = SECTIONS.filter((s) => !s.adminOnly || isAdmin);
  const section: SectionKey = visibleSections.some((s) => s.key === requested) ? (requested as SectionKey) : 'permissions';
  const readOnly = !isAdmin;

  const apply = (data: FeedSettingsView) => {
    setSettings(data);
    setFeedSettingsCache(data);
    const values = toForm(data);
    setInitial(values);
    form.resetFields();
    form.setFieldsValue(values);
    setDirtyCount(0);
  };

  useEffect(() => {
    let alive = true;
    AxiosUtils.get(API.feedAdminSettings)
      .then((res: any) => {
        if (!alive) return;
        apply(res?.data as FeedSettingsView);
        setLoadError(null);
      })
      .catch((err) => alive && setLoadError(feedError(err)));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick]);

  const goSection = (key: SectionKey, field?: string | (string | number)[]) => {
    set({ section: key === 'permissions' ? undefined : key });
    if (field) window.setTimeout(() => form.scrollToField(field, { block: 'center' }), 80);
  };

  const onValuesChange = (changed: Record<string, any>) => {
    // Bỏ "Xem" thì 3 ô còn lại tự bỏ (giống normalizePerm của backend).
    if (changed.permissions) {
      Object.keys(changed.permissions).forEach((cls) => {
        if (changed.permissions[cls]?.view === false) {
          form.setFieldValue(['permissions', cls, 'post'], false);
          form.setFieldValue(['permissions', cls, 'comment'], false);
          form.setFieldValue(['permissions', cls, 'react'], false);
        }
      });
    }
    setDirtyCount(Object.keys(diffValues(initial, form.getFieldsValue(true))).length);
  };

  const save = async () => {
    try {
      await form.validateFields();
    } catch (e: any) {
      const field = e?.errorFields?.[0]?.name?.[0];
      if (field && FIELD_SECTION[field]) goSection(FIELD_SECTION[field], e.errorFields[0].name);
      return;
    }
    const body = diffValues(initial, form.getFieldsValue(true));
    if (!Object.keys(body).length) {
      toast.info('Chưa có thay đổi.');
      return;
    }
    setSaving(true);
    try {
      const res: any = await AxiosUtils.put(API.feedAdminSettings, body);
      apply(res?.data as FeedSettingsView);
      toast.success('Đã lưu cài đặt Bảng tin.');
      afterAction();
      reloadAccess();
    } catch (err: any) {
      if (errorCode(err) === 'FEED_SETTINGS_INVALID' && err?.reason === 'TERMS_MISSING_AI_CLAUSE') {
        goSection('terms', 'termsHasAiClause');
        toast.error(
          'Quy tắc đang dùng chưa có câu về việc gửi nội dung tới AI. Thêm câu đó (nút “Chèn câu mẫu về AI”) và tick xác nhận trước khi bật AI.',
          6
        );
      } else if (errorCode(err) === 'FEED_SETTINGS_INVALID' && err?.reason === 'NO_PROVIDER') {
        goSection('ai', typeof err?.field === 'string' ? err.field : undefined);
        toast.error('Chưa cấu hình nhà cung cấp AI trên máy chủ (khoá API).', 6);
      } else {
        const field = typeof err?.field === 'string' ? err.field : undefined;
        if (field && FIELD_SECTION[field]) goSection(FIELD_SECTION[field], field);
        toast.error(feedError(err));
      }
    } finally {
      setSaving(false);
    }
  };

  const undo = () => {
    form.resetFields();
    form.setFieldsValue(initial);
    setDirtyCount(0);
  };

  if (loadError && !settings) {
    return (
      <Alert
        type="error"
        showIcon
        message="Không tải được cài đặt Bảng tin"
        description={loadError}
        action={<Button onClick={() => setTick((t) => t + 1)}>Thử lại</Button>}
      />
    );
  }
  if (!settings) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <Spin />
      </div>
    );
  }

  const show = (key: SectionKey): React.CSSProperties => (section === key ? {} : { display: 'none' });
  const hardVideo = settings.videoHardMaxMb || 300;
  const hardImage = settings.imageHardMaxMb || 20;
  const ffmpegBad = settings.ffmpeg && (!settings.ffmpeg.available || !settings.ffmpeg.libx264);
  const storage = summary?.storageBytes ?? settings.storageBytes;

  return (
    <div>
      {readOnly && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Chỉ Admin sửa được cài đặt Bảng tin. Bạn đang xem ở chế độ chỉ đọc."
        />
      )}
      <div style={{ marginBottom: 16, overflowX: 'auto' }}>
        <Segmented
          value={section}
          options={visibleSections.map((s) => ({ value: s.key, label: s.label }))}
          onChange={(v) => goSection(v as SectionKey)}
        />
      </div>

      <Form form={form} layout="vertical" disabled={readOnly} onValuesChange={onValuesChange}>
        {/* ---------------- Quyền */}
        <div style={show('permissions')}>
          <h3 className={styles.sectionTitle}>Ai được làm gì</h3>
          <PermissionsTable readOnly={readOnly} />
          <div className={styles.hint} style={{ margin: '8px 0 20px' }}>
            Đổi có hiệu lực ngay ở lần mở app kế tiếp. Người đang bị hạn chế vẫn bị hạn chế.
          </div>
          <Form.Item
            label="Giữ bài chờ duyệt"
            name="holdOnReview"
            extra="Tắt (khuyên dùng — đúng quyết định “đăng thẳng”): bài dính từ “cần xem” hoặc AI “cần xem” vẫn đăng, chỉ vào tab Cần xem để quản lý xem lại. Bật: các bài đó bị giữ, chỉ tác giả thấy, tới khi quản lý duyệt."
          >
            <ConfirmSwitch
              disabled={readOnly}
              confirm={{
                title: 'Bật giữ bài chờ duyệt?',
                content:
                  'Bài của mọi người (trừ nhóm miễn dưới đây) dính từ “cần xem” sẽ bị giữ lại chờ duyệt. Bật?',
              }}
            />
          </Form.Item>
          <Form.Item
            label="Nhóm được miễn"
            name="reviewExempt"
            extra="Nội dung của nhóm được chọn dính từ “cần xem” hoặc AI “cần xem” không bị gắn cờ, không bị giữ, chỉ ghi vào Nhật ký. Từ “chặn hẳn” và AI “chặn” áp dụng cho tất cả."
          >
            <Checkbox.Group options={EXEMPT_OPTIONS} />
          </Form.Item>
        </div>

        {/* ---------------- Ảnh & video */}
        <div style={show('media')}>
          <Form.Item
            label="Cho đăng video"
            name="videoEnabled"
            valuePropName="checked"
            extra="Tắt: app ẩn nút chọn video, video đã đăng vẫn xem được."
          >
            <Switch />
          </Form.Item>
          <Form.Item
            label="Video dài tối đa"
            name="videoMaxSeconds"
            rules={[{ type: 'number', min: 5, max: 600, message: 'Từ 5 đến 600 giây.' }]}
            extra="Thời gian thử nghiệm: 60 giây. Đây là số quyết định chi phí lưu trữ: mỗi video lưu ≈ số giây × 4,5 Mbps (60 giây ≈ 34 MB) và tốn thời gian xử lý tương ứng."
          >
            <InputNumber min={5} max={600} addonAfter="giây" style={{ width: 160 }} />
          </Form.Item>
          <Form.Item
            label="Dung lượng video tải lên tối đa"
            name="videoMaxMb"
            rules={[{ type: 'number', min: 5, max: hardVideo, message: `Từ 5 đến ${hardVideo} MB.` }]}
            extra={`Trần do máy chủ đặt: ${hardVideo} MB. Không ảnh hưởng chi phí lưu trữ (máy chủ luôn nén về 720p), chỉ ảnh hưởng băng thông tải lên. Android quay 1080p 60 giây nặng 120–150 MB, iPhone 4K ≈ 170 MB — dưới 200 MB sẽ chặn nhiều video 60 giây.`}
          >
            <InputNumber min={5} max={hardVideo} addonAfter="MB" style={{ width: 160 }} />
          </Form.Item>
          <Form.Item
            label="Số ảnh tối đa mỗi bài"
            name="imageMaxCount"
            rules={[{ type: 'number', min: 1, max: 10, message: 'Từ 1 đến 10.' }]}
          >
            <InputNumber min={1} max={10} addonAfter="ảnh" style={{ width: 160 }} />
          </Form.Item>
          <Form.Item
            label="Dung lượng mỗi ảnh tối đa"
            name="imageMaxMb"
            rules={[{ type: 'number', min: 1, max: hardImage, message: `Từ 1 đến ${hardImage} MB.` }]}
            extra={`Trần do máy chủ đặt: ${hardImage} MB.`}
          >
            <InputNumber min={1} max={hardImage} addonAfter="MB" style={{ width: 160 }} />
          </Form.Item>
          <div className={styles.box}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>Máy chủ xử lý video</div>
            <div style={{ fontSize: 13 }}>
              {settings.ffmpeg
                ? `ffmpeg ${settings.ffmpeg.version || '(không rõ phiên bản)'} · libx264: ${
                    settings.ffmpeg.libx264 ? 'có' : 'không'
                  }`
                : 'Máy chủ chưa báo thông tin ffmpeg.'}
            </div>
            {ffmpegBad && (
              <Alert
                type="error"
                showIcon
                style={{ marginTop: 8 }}
                message="Máy chủ chưa cài ffmpeg (hoặc thiếu libx264). App sẽ không cho đăng video dù công tắc đang bật. Cài: sudo apt-get install -y ffmpeg."
              />
            )}
            <div style={{ fontSize: 13, marginTop: 6 }}>
              Video treo quá 30 phút: {summary?.stuckMedia ?? '—'} · Video lỗi 24 giờ qua: {summary?.failedMedia24h ?? '—'}
            </div>
            {storage !== undefined && storage !== null && (
              <div style={{ fontSize: 13, marginTop: 4 }}>
                Bảng tin đang dùng <strong>{fmtBytes(storage) || '0 B'}</strong> lưu trữ.
              </div>
            )}
            {settings.cdnPurgeConfigured === false && (
              <Alert
                type="warning"
                showIcon
                style={{ marginTop: 8 }}
                message="Chưa cấu hình xoá cache CDN (DO_API_TOKEN) — ảnh/video bị gỡ có thể còn xem được qua link cũ tới 1 giờ."
              />
            )}
          </div>
        </div>

        {/* ---------------- Giới hạn & báo cáo */}
        <div style={show('limits')} className={styles.narrow}>
          <Form.Item
            label="Độ dài bài tối đa (ký tự)"
            name="textMaxLen"
            rules={[{ type: 'number', min: 100, max: 10000, message: 'Từ 100 đến 10 000.' }]}
          >
            <InputNumber min={100} max={10000} style={{ width: 200 }} />
          </Form.Item>
          <Form.Item
            label="Độ dài bình luận tối đa (ký tự)"
            name="commentMaxLen"
            rules={[{ type: 'number', min: 50, max: 2000, message: 'Từ 50 đến 2 000.' }]}
          >
            <InputNumber min={50} max={2000} style={{ width: 200 }} />
          </Form.Item>
          <Form.Item
            label="Mỗi người đăng tối đa (bài/giờ)"
            name="postsPerHour"
            extra="Quản lý không bị giới hạn."
            rules={[{ type: 'number', min: 1, max: 200, message: 'Từ 1 đến 200.' }]}
          >
            <InputNumber min={1} max={200} style={{ width: 200 }} />
          </Form.Item>
          <Form.Item
            label="Mỗi người bình luận tối đa (bình luận/giờ)"
            name="commentsPerHour"
            rules={[{ type: 'number', min: 5, max: 1000, message: 'Từ 5 đến 1 000.' }]}
          >
            <InputNumber min={5} max={1000} style={{ width: 200 }} />
          </Form.Item>
          <Form.Item
            label="Tự ẩn khi có … người khác nhau báo cáo"
            name="reportAutoHideThreshold"
            extra="0 = tắt. Không áp dụng cho bài tên HP Media và nội dung của Quản lý. Báo cáo vẫn nằm trong hàng chờ để quản lý quyết."
            rules={[{ type: 'number', min: 0, max: 50, message: 'Từ 0 đến 50.' }]}
          >
            <InputNumber min={0} max={50} addonAfter="người" style={{ width: 200 }} />
          </Form.Item>
          <Form.Item
            label="Bài Quan trọng hiện ở đầu Bảng tin trong (ngày)"
            name="importantDefaultDays"
            rules={[{ type: 'number', min: 1, max: 30, message: 'Từ 1 đến 30.' }]}
          >
            <InputNumber min={1} max={30} addonAfter="ngày" style={{ width: 200 }} />
          </Form.Item>
          <Form.Item
            label="Số bài Quan trọng được gửi thông báo đẩy mỗi ngày"
            name="importantPushPerDay"
            extra="0 = không gửi."
            rules={[{ type: 'number', min: 0, max: 20, message: 'Từ 0 đến 20.' }]}
          >
            <InputNumber min={0} max={20} addonAfter="bài" style={{ width: 200 }} />
          </Form.Item>
        </div>

        {/* ---------------- Kiểm duyệt AI */}
        <div style={show('ai')}>
          <AiSection form={form} settings={settings} readOnly={readOnly} isAdmin={isAdmin} />
        </div>

        {/* ---------------- Điều khoản & trang HP Media */}
        <div style={show('terms')}>
          <Form.Item
            label="Tên trang khi đăng bài tên công ty"
            name="pageName"
            rules={[
              { required: true, whitespace: true, message: 'Nhập tên trang.' },
              { max: 80, message: 'Tối đa 80 ký tự.' },
            ]}
            className={styles.narrow}
          >
            <Input maxLength={80} />
          </Form.Item>
          <Form.Item noStyle shouldUpdate={(a, b) => a.pageName !== b.pageName}>
            {({ getFieldValue }) => (
              <Form.Item label="Ảnh đại diện trang" name="pageAvatarUrl">
                <AvatarUrlInput disabled={readOnly} name={getFieldValue('pageName')} />
              </Form.Item>
            )}
          </Form.Item>
          <Form.Item label="Quy tắc đăng bài" name="termsText">
            <TermsTextInput disabled={readOnly} defaultTerms={settings.defaultTerms} />
          </Form.Item>
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) => {
              const text = getFieldValue('termsText');
              const custom = text !== null && text !== undefined;
              const textChanged = !same(initial.termsText, text);
              const version = settings.termsVersion ?? 1;
              return (
                <div style={{ marginBottom: 16 }}>
                  {custom && (
                    <Form.Item name="termsHasAiClause" valuePropName="checked" style={{ marginBottom: 8 }}>
                      <Checkbox>
                        Văn bản này có câu về việc gửi nội dung tới AI ở nước ngoài — bắt buộc tick trước khi bật AI
                        (máy chủ kiểm).
                      </Checkbox>
                    </Form.Item>
                  )}
                  <div style={{ fontSize: 13 }}>
                    Phiên bản hiện tại: <strong>v{version}</strong>
                    {settings.termsTextIsDefault ? ' (mặc định)' : ''}
                  </div>
                  {textChanged ? (
                    <div className={styles.hint} style={{ color: colors.warningInk }}>
                      Lưu sẽ tạo phiên bản v{version + 1}; mọi người phải đồng ý lại.
                    </div>
                  ) : (
                    <Form.Item name="bumpTermsVersion" valuePropName="checked" style={{ margin: '6px 0 0' }}>
                      <Checkbox>Vẫn tạo phiên bản mới (buộc mọi người đồng ý lại)</Checkbox>
                    </Form.Item>
                  )}
                </div>
              );
            }}
          </Form.Item>
          <Collapse
            items={[
              {
                key: 'versions',
                label: 'Lịch sử phiên bản Quy tắc (ai đã đồng ý văn bản nào)',
                children: <TermsVersions />,
              },
            ]}
          />
        </div>
      </Form>

      {/* ---------------- Từ cấm / Máy chủ: nút riêng, ngoài form */}
      <div style={show('words')}>
        <WordsCard />
      </div>
      {isAdmin && (
        <div style={show('server')}>
          <MaintenanceSection />
        </div>
      )}

      {!readOnly && section !== 'words' && section !== 'server' && (
        <div className={styles.footer}>
          <div>
            <div style={{ fontWeight: 600 }}>{dirtyCount ? `${dirtyCount} mục đã đổi` : 'Chưa có thay đổi'}</div>
            {settings.updatedAt && (
              <div className={styles.muted}>
                Sửa lần cuối {fmtDateTime(settings.updatedAt)}
                {settings.updatedByName ? ` bởi ${settings.updatedByName}` : ''}
              </div>
            )}
          </div>
          <div className={styles.footerActions}>
            <Button onClick={undo} disabled={!dirtyCount || saving}>
              Hoàn tác
            </Button>
            <Button type="primary" onClick={save} loading={saving}>
              Lưu cài đặt
            </Button>
          </div>
        </div>
      )}
      {readOnly && settings.updatedAt && (
        <div className={styles.muted} style={{ marginTop: 12 }}>
          Sửa lần cuối {fmtDateTime(settings.updatedAt)}
          {settings.updatedByName ? ` bởi ${settings.updatedByName}` : ''}
        </div>
      )}
    </div>
  );
};

export default SettingsTab;
